import { Spinner } from '@change/ds-core-ui';

import { useKnownAvailableParams } from '@change-corgi/core/react/router';

import { withPrefetchedData } from 'src/shared/prefetch';

import { ImpactStoryPageContextProvider, prefetchPageContext } from 'src/app/pages/impactStory/pageContext';
import type { ImpactStoryPagePrefetchedDataProps } from 'src/app/pages/impactStory/pageContext';
import { addSchemeToUrl } from 'src/app/shared/utils/assets';
import { isLoaded } from 'src/app/shared/utils/async';

import { Layout } from '../layout';

import { ImpactStoryErrorHandler } from './ErrorHandler';

const DEFAULT_IMAGE = 'https://static.change.org/images/default-petition.gif';
const DEFAULT_IMAGE_WIDTH = 1120;
const DEFAULT_IMAGE_HEIGHT = 630;

export const ImpactStory = withPrefetchedData(
	function ImpactStory({ prefetchedData }: ImpactStoryPagePrefetchedDataProps) {
		const { slug } = useKnownAvailableParams<{ slug: string }>();

		return (
			<ImpactStoryPageContextProvider
				slug={slug}
				prefetchedData={prefetchedData}
				errorHandler={ImpactStoryErrorHandler}
				loadingFallback={<Spinner className="mx-auto my-64" />}
			>
				<Layout />
			</ImpactStoryPageContextProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchData: async (context) => prefetchPageContext(context.params.slug!, context),
		pageTitle: ({ prefetchedData, fromTemplate }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			return fromTemplate(prefetchedData.pageData.pageData.featureConfig.story.title);
		},
		pageCanonical: ({ prefetchedData }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			return `/impact-stories/${prefetchedData.pageData.pageData.petition.slug}`;
		},
		pageMetadata: ({ prefetchedData }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return {};
			}
			const {
				featureConfig: {
					story: { title, quoteImage },
				},
			} = prefetchedData.pageData.pageData;

			const storyImageUrl = quoteImage ? addSchemeToUrl(quoteImage) : null;
			return {
				ogTitle: title,
				ogType: 'website',
				twitterCard: 'summary',
				twitterImageAlt: title,
				...(storyImageUrl
					? {
							ogImage: storyImageUrl,
							ogImageSecureUrl: storyImageUrl,
							twitterImage: storyImageUrl,
						}
					: {
							ogImage: DEFAULT_IMAGE,
							ogImageSecureUrl: DEFAULT_IMAGE,
							ogImageWidth: DEFAULT_IMAGE_WIDTH,
							ogImageHeight: DEFAULT_IMAGE_HEIGHT,
							twitterImage: DEFAULT_IMAGE,
						}),
			};
		},
	},
);
